import NoRecords from '../../shared/NoRecords';
import React, { useMemo } from 'react';
import PaginatedTable from '../PaginatedTable';
import CsvButton from '../CsvButton';
import { fmtCount, fmtDate, fmtSearchTerm } from '../lib/format';

/**
 * Evidence for `new_queries`.
 *
 * `facts`: { items: [{ query, first_seen, clicks, impressions }],
 *            summary: { new_query_count, new_query_clicks, window_days, reliable } }
 *
 * Rows are sorted by visits (clicks) descending, then by appearances — the §E order for
 * this panel, which overrides the general "render backend order" default (F1). The payload
 * arrives ranked by appearances alone, which leads the panel with zero-visit noise
 * ("tv channels a-z filetype:pdf", "what country is camp verde"), so the most encouraging
 * panel on the page reads as junk. Sorting on visits puts the searches that actually
 * brought people in at the top. Queries still render verbatim and lowercase as Google
 * returned them; title-casing them would misrepresent what somebody typed.
 *
 * `summary.reliable === false` means the property's history is shorter than the window,
 * so every query looks new. The caption says so plainly — the confidence will be
 * `insufficient` in that case anyway, which means this component won't render at all,
 * but the caption covers a `reliable: false` that arrives at a higher confidence.
 *
 * Collapsed shows the count's first five rows; expanded shows the full list with
 * first-seen dates and a CSV (spec v2 §7).
 */

/** How many rows the collapsed card shows before "Show more" — enough to fill the
 *  full-width card with real searches rather than leaving it short, without turning the
 *  collapsed glance into the whole list. */
const COLLAPSED_ROWS = 15;

/** Export columns — the table's own labels, word for word. See `lib/csv.js`. */
const CSV_COLUMNS = [
  { header: 'Search', get: (r) => fmtSearchTerm(r.query) },
  { header: 'First seen', get: (r) => r.first_seen },
  { header: 'Showed up in searches', get: (r) => r.impressions },
  { header: 'Visits', get: (r) => r.clicks },
];

/** One collapsed row — a button that drills when the row carries a `drill` (§E.4). */
const NewQueryRow = ({ row, onDrill }) => {
  const drillable = Boolean(row?.drill) && typeof onDrill === 'function';
  const body = (
    <>
      <span className="text-sm text-gray-800 dark:text-gray-200 break-words min-w-0">{fmtSearchTerm(row.query)}</span>
      {fmtDate(row.first_seen) && (
        <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">first seen {fmtDate(row.first_seen)}</span>
      )}
    </>
  );
  if (drillable) {
    return (
      <li>
        <button
          type="button"
          data-query={row.query}
          onClick={() => onDrill(row.drill)}
          className="w-full flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2 px-2 -mx-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset transition-colors"
        >
          {body}
        </button>
      </li>
    );
  }
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2" data-query={row.query}>
      {body}
    </li>
  );
};

const NewQueryList = ({ facts, sentence, collapsed = true, onDrill }) => {
  // Visits (clicks) desc, then appearances (impressions) desc — see the header note. A copy,
  // so the payload order is never mutated. `clicks`/`impressions` missing sort as 0.
  const items = useMemo(() => {
    const rows = Array.isArray(facts?.items) ? [...facts.items] : [];
    return rows.sort((a, b) =>
      (Number(b.clicks) || 0) - (Number(a.clicks) || 0)
      || (Number(b.impressions) || 0) - (Number(a.impressions) || 0));
  }, [facts]);
  const summary = facts?.summary || {};

  const columns = [
    { key: 'query', label: 'Search', render: (r) => <span className="font-normal text-gray-900 dark:text-gray-100">{fmtSearchTerm(r.query)}</span> },
    { key: 'first_seen', label: 'First seen', render: (r) => fmtDate(r.first_seen) },
    { key: 'impressions', label: 'Showed up in searches', align: 'right', render: (r) => fmtCount(r.impressions) },
    { key: 'clicks', label: 'Visits', align: 'right', render: (r) => fmtCount(r.clicks) },
  ];

  return (
    <div className="space-y-3">
      {summary.reliable === false && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          We have not been collecting long enough to tell a genuinely new search from one
          that was always there, so treat this list as a first look.
        </p>
      )}
      {/* §7 collapsed: "Count, first five" — a list, not a spreadsheet. It used to render
          the full four-column table truncated to five rows, which put column headers,
          right-aligned counts and a date grid on a card whose whole point is "these
          searches are new". The figures still exist; they are one click away. */}
      {collapsed ? (
        items.length === 0 ? (
          <NoRecords />
        ) : (
          <ul data-testid="new-query-list" className="divide-y divide-gray-100 dark:divide-gray-800">
            {items.slice(0, COLLAPSED_ROWS).map((r, i) => (
              <NewQueryRow key={`${r.query}-${i}`} row={r} onDrill={onDrill} />
            ))}
          </ul>
        )
      ) : (
        <>
          <PaginatedTable
            columns={columns}
            rows={items}
            rowKey={(r, i) => `${r.query}-${i}`}
            label="searches"
            ariaLabel={sentence}
          />
          <CsvButton panelKey="new_queries" name="New kinds of searches finding you" columns={CSV_COLUMNS} rows={items} />
        </>
      )}
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * Collapsed shows the first five. Expanding reveals the rest, and always adds the CSV —
 * so a list of three still changes when opened, while an empty one does not.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
NewQueryList.hasDepth = (facts) => Array.isArray(facts?.items) && facts.items.length > 0;


export default NewQueryList;
